import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Kicker } from '@/components/Kicker';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSync() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const runsQ = useQuery({ queryKey: ['admin', 'sync', 'runs'], queryFn: async () => (await api.get('/admin/sync/runs')).data });
  const runMut = useMutation({
    mutationFn: async () => (await api.post('/admin/sync', {})).data,
    onSuccess: (res) => {
      if (res.status === 'success') toast.success(t('admin.sync.success'));
      else toast.error(t('admin.sync.failed'));
      qc.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: () => toast.error(t('admin.sync.failed')),
  });

  const runs = runsQ.data?.items || [];
  const last = runs[0];

  return (
    <section>
      <Kicker>{t('admin.title')}</Kicker>
      <h1 className="mt-3 font-display text-3xl font-semibold text-[#14171C]">{t('admin.nav.sync')}</h1>

      <div className="mt-8 pn-card p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-[#4A4D52]">{t('admin.sync.last_run')}</div>
            <div className="mt-1 font-display text-2xl font-semibold">{last ? new Date(last.started_at).toLocaleString() : '—'}</div>
            <div className="mt-1 text-sm text-[#4A4D52]">
              {last ? `${last.status} · ${last.duration_ms ?? '?'} ms · ${last.triggered_by || ''}` : '—'}
            </div>
          </div>
          <button
            onClick={() => runMut.mutate()}
            disabled={runMut.isPending}
            data-testid="admin-sync-trigger-button"
            className="pn-btn-orange disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${runMut.isPending ? 'animate-spin' : ''}`} />
            {runMut.isPending ? t('admin.sync.in_progress') : t('admin.sync.trigger')}
          </button>
        </div>
      </div>

      <div className="mt-6 pn-card overflow-hidden">
        <table className="w-full text-left text-sm" data-testid="admin-sync-runs-table">
          <thead className="bg-[#F6F8FB] text-xs uppercase tracking-wide text-[#4A4D52]">
            <tr>
              <th className="px-4 py-3">Start</th>
              <th className="px-4 py-3">{t('admin.sync.duration')}</th>
              <th className="px-4 py-3">{t('admin.sync.status')}</th>
              <th className="px-4 py-3">{t('admin.sync.triggered_by')}</th>
              <th className="px-4 py-3">Statistiken</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-t border-[#E4E9F0] hover:bg-[#F6F8FB]">
                <td className="px-4 py-3 font-mono text-xs">{new Date(r.started_at).toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.duration_ms ?? '—'} ms</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${r.status === 'success' ? 'bg-[#E7F6EE] text-[#1F7A4D]' : 'bg-[#FBEBEA] text-[#B42318]'}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3 text-xs">{r.triggered_by}</td>
                <td className="px-4 py-3 text-xs font-mono text-[#4A4D52]">{r.stats ? JSON.stringify(r.stats) : '—'}</td>
              </tr>
            ))}
            {runs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[#4A4D52]">{t('catalog.empty')}</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
